











































import { Component, Vue, Watch } from "vue-property-decorator";
import Layout from "@/components/Layout.vue";
import EmployerSelector from "@/components/EmployerSelector.vue";
import { createNamespacedHelpers } from "vuex";
import { EmployerHierarchy } from "@/store/modules/persistent/persistentTypes";
import TextField from "@/form/TextField.vue";
import SelectField from "@/form/SelectField.vue";
import GridFilter from "@/components/GridFilter.vue";
import Button from "@/form/Button.vue";
import { isOnlyOneReportingCentreSelected } from "@/utils/EmployerSelectorUtils";
import Grid from "@/grid/Grid.vue";
import {
	ColDef,
	ColGroupDef,
	ICellRendererParams,
	IServerSideDatasourceTyped,
	IServerSideGetRowsParamsTyped,
} from "ag-grid-community";
import NewsWidget from "@/components/NewsWidget.vue";
import Card from "@/components/Card.vue";
import { hasPermission, RC } from "@/utils/PermissionUtils";
import axios, { axiosStatic } from "@/utils/ApiUtils";
import { PagedResult } from "@/grid/gridTypes";
import { nppCancelMandates, nppGetMandates } from "@/constants/apiconstants";
import { toastErrorMessage, toastInfoMessage } from "@/plugins/toasts";
import { parseErrorMessage } from "@/utils/ErrorUtils";
import GridActionsRenderer from "@/grid/GridActionsRenderer.vue";
import { RoutePath } from "@/router/routePath";
import {
	capitalise,
	columnCurrencyFormatter,
	columnDateTimeFormatter,
} from "@/utils/CommonUtils";
import { PayToMandate } from "@/pages/maintenanceTypes";
import { TSelectLevel } from "@/components/employerSelectorTypes";
import ModalWithSaveAndCancel from "@/components/ModalWithSaveAndCancel.vue";
import { ValueFormatterParams } from "ag-grid-community/dist/lib/entities/colDef";

const { mapState } = createNamespacedHelpers("persistent");

@Component({
	components: {
		ModalWithSaveAndCancel,
		Card,
		NewsWidget,
		Grid,
		Button,
		GridFilter,
		SelectField,
		TextField,
		EmployerSelector,
		Layout,
	},
	computed: mapState(["selectedEntities", "employerHierarchy"]),
})
export default class PayToMandatesPage
	extends Vue
	implements IServerSideDatasourceTyped
{
	/**
	 * Type the mapped persistent.selectedEntities getter.
	 * It is a computed property. Do not mutate it.
	 */
	selectedEntities!: string[];

	/**
	 * Type the mapped persistent.employerHierarchy getter.
	 * It is a computed property. Do not mutate it.
	 */
	employerHierarchy!: EmployerHierarchy[];
	private errorMessage: string | null = null;

	private pageContext: TSelectLevel = "ALL";

	private gridReady = false;

	public $refs!: {
		gridEl: Grid;
	};
	private gridVMList: Vue[] = [];
	cancelModal = false;
	cancelMandateId: string | undefined = undefined;

	private onGridReady() {
		this.gridReady = true;
	}

	private readonly columnDefs: (ColGroupDef | ColDef)[] = [
		{
			headerName: "Agreement ID",
			field: "mandateId",
			resizable: true,
			width: 300,
		},
		{
			headerName: "BSB",
			field: "bsb",
			resizable: true,
			width: 100,
		},
		{
			headerName: "Account number",
			field: "accountNumber",
			resizable: true,
			width: 150,
		},
		{
			headerName: "Account name",
			field: "accountOwnerName",
			resizable: true,
			width: 150,
		},
		{
			headerName: "Max amount",
			field: "maxAmount",
			resizable: true,
			width: 150,
			valueFormatter: columnCurrencyFormatter,
		},
		{
			headerName: "Status",
			field: "state",
			valueFormatter: (params) => this.mapToLabel(params),
			resizable: true,
			width: 150,
		},
		{
			headerName: "Modified date",
			field: "modifiedDate",
			valueFormatter: columnDateTimeFormatter,
			resizable: true,
			width: 200,
		},
		{
			headerName: "Actions",
			field: "__Actions",
			cellRenderer: this.actionsRender,
			minWidth: 100,
			maxWidth: 100,
		},
	];

	clearErrorMessage() {
		this.errorMessage = null;
	}

	back() {
		this.$router.push(RoutePath.Maintenance);
	}

	actionsRender(params: ICellRendererParams): HTMLElement {
		const vm = new Vue({
			el: document.createElement("div"),
			render: (createElement) => {
				return createElement(GridActionsRenderer, {
					props: {
						rowIndex: params.rowIndex,
						row: params.data,
						isView: false,
						isEdit: false,
						isDelete: hasPermission("EDIT_PAYMENT_PROFILE"),
						showDeleteDisabled: params.data.state === "cancelled",
					},
					on: {
						clickDelete: this.onClickDelete,
					},
				});
			},
		});

		this.gridVMList.push(vm);
		return vm.$el as HTMLElement;
	}

	addNewPayTo() {
		if (this.selectedEntities.length <= 0) {
			this.errorMessage = "Please select a reporting centre";
			return;
		}

		if (
			!isOnlyOneReportingCentreSelected(
				this.selectedEntities,
				this.employerHierarchy
			)
		) {
			this.errorMessage = "Please select only one reporting centre";
			return;
		}

		this.$router.push({
			name: "Add new PayTo agreement",
			params: { rcId: this.selectedEntities[0] },
		});
	}

	get entitySelection(): string | null {
		if (this.selectedEntities.length !== 1) {
			return null;
		}
		return this.selectedEntities[0];
	}

	get entityRcId() {
		if (this.entitySelection && this.entitySelection.startsWith(RC)) {
			return parseInt(this.entitySelection.substr(2), 10);
		}
		return 0;
	}

	public refreshGrid(): void {
		this.gridReady = false;
		if (!this.$refs.gridEl) {
			return;
		}
		this.$refs.gridEl.refresh();
		this.gridReady = true;
	}

	@Watch("entitySelection", { immediate: true })
	validateSelection() {
		if (
			this.entitySelection === null ||
			!this.entitySelection.startsWith(RC)
		) {
			this.errorMessage = "Please select a Reporting centre";
		} else {
			this.refreshGrid();
		}
	}

	mapToLabel(params: ValueFormatterParams): string {
		if (
			params.value === undefined ||
			params.value === null ||
			params.value === ""
		) {
			return "";
		}
		if (
			["CREATED", "AWAITING_AUTHORISATION"].includes(
				params.value.toUpperCase()
			)
		) {
			return "Pending";
		}
		if ("CANCELED" === params.value.toUpperCase()) {
			return "Cancelled";
		}
		return capitalise(params.value);
	}

	getRows(params: IServerSideGetRowsParamsTyped<PayToMandate>): void {
		if (this.entityRcId === 0) {
			params.successCallback([]);
			return;
		}

		axios
			.get<PagedResult<PayToMandate>>(nppGetMandates(this.entityRcId), {
				params: {
					// Pass in <Grid> parameters
					grid: params.request,
				},
				cancelToken: params.cancelToken,
			})
			.then((response) => {
				const pagedResultData = response.data;
				params.successCallback(pagedResultData);
			})
			.catch((error) => {
				if (axiosStatic.isCancel(error)) {
					return;
				}
				toastErrorMessage(parseErrorMessage(error));
			});
	}

	onClickDelete({ row }: { row: PayToMandate }) {
		this.cancelModal = true;
		this.cancelMandateId = row.mandateId;
	}

	confirmCancel() {
		if (!this.cancelMandateId) {
			return;
		}

		axios
			.delete<PagedResult<PayToMandate>>(
				nppCancelMandates(this.entityRcId, this.cancelMandateId)
			)
			.then(() => {
				toastInfoMessage("Cancellation of mandate has been initiated");
				this.cancelModal = false;
				this.$refs.gridEl.reload();
			})
			.catch((error) => {
				if (axiosStatic.isCancel(error)) {
					return;
				}
				toastErrorMessage(parseErrorMessage(error));
			});
	}
}
